import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Lock, Shield, Wallet, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import PublicLayout from '@/components/PublicLayout';
import { cn } from '@/lib/utils';

const trustItems = [
  {
    icon: Lock,
    title: 'Bank-grade encryption',
    description: 'Your data is encrypted at rest and in transit.',
  },
  {
    icon: Shield,
    title: 'Trusted platform',
    description: 'Join thousands managing their wealth with Hoito.',
  },
  {
    icon: Wallet,
    title: 'Your data, yours',
    description: 'Export anytime. We never sell your information.',
  },
];

function getPasswordStrength(password: string) {
  if (!password) return { score: 0, label: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];
  return { score, label: labels[score] };
}

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);

  const requirements = useMemo(
    () => [
      { met: password.length >= 8, label: 'At least 8 characters' },
      { met: /[a-z]/.test(password) && /[A-Z]/.test(password), label: 'Upper and lowercase' },
      { met: /\d/.test(password), label: 'At least one number' },
      { met: /[^a-zA-Z0-9]/.test(password), label: 'At least one symbol (optional)' },
    ],
    [password]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const { needsEmailConfirmation } = await signUp(email, password, fullName);
      if (needsEmailConfirmation) {
        setSuccess('Account created! Please check your email to confirm your account, then sign in.');
      } else {
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PublicLayout>
      <main className="pt-20 pb-14 lg:pt-28 lg:pb-20 min-h-[calc(100vh-200px)] flex items-center">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Form section */}
            <div className="order-2 lg:order-1">
              <div className={cn('max-w-md mx-auto lg:mx-0', 'animate-fade-in')}>
                <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm hover:shadow-lg hover:border-slate-300/80 transition-all duration-300 p-6 sm:p-8">
                  <div className="mb-6">
                    <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
                      Create your account
                    </h1>
                    <p className="mt-2 text-slate-600">
                      Join Hoito and start managing your wealth with clarity.
                    </p>
                  </div>

                  {error && (
                    <Alert variant="destructive" className="mb-6 border-red-200 bg-red-50">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-red-800">{error}</AlertDescription>
                    </Alert>
                  )}

                  {success && (
                    <Alert className="mb-6 border-brand-200 bg-brand-50 [&>svg]:text-brand-900">
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription className="text-brand-900">{success}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="fullName" className="text-slate-900 font-medium">
                        Full name
                      </Label>
                      <Input
                        id="fullName"
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Enter your full name"
                        className={cn(
                          'h-11 border-slate-200 text-slate-900 placeholder:text-slate-400',
                          'focus-visible:ring-2 focus-visible:ring-brand-900/20 focus-visible:border-brand-900',
                          'transition-all duration-200 rounded-lg'
                        )}
                        required
                        autoComplete="name"
                        disabled={isLoading}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-900 font-medium">
                        Email address
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className={cn(
                          'h-11 border-slate-200 text-slate-900 placeholder:text-slate-400',
                          'focus-visible:ring-2 focus-visible:ring-brand-900/20 focus-visible:border-brand-900',
                          'transition-all duration-200 rounded-lg'
                        )}
                        required
                        autoComplete="email"
                        disabled={isLoading}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-slate-900 font-medium">
                        Password
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Choose a strong password"
                          className={cn(
                            'h-11 pr-11 border-slate-200 text-slate-900 placeholder:text-slate-400',
                            'focus-visible:ring-2 focus-visible:ring-brand-900/20 focus-visible:border-brand-900',
                            'transition-all duration-200 rounded-lg'
                          )}
                          required
                          autoComplete="new-password"
                          minLength={8}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className={cn(
                            'absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md',
                            'text-slate-400 hover:text-slate-600 hover:bg-slate-100',
                            'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-900/20 focus:ring-offset-1'
                          )}
                          tabIndex={-1}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      {/* Password strength indicator */}
                      {password && (
                        <div className="space-y-2 pt-1">
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <div
                                key={i}
                                className={cn(
                                  'h-1 flex-1 rounded-full transition-colors duration-200',
                                  i <= passwordStrength.score
                                    ? passwordStrength.score <= 2
                                      ? 'bg-amber-500'
                                      : passwordStrength.score <= 4
                                        ? 'bg-brand-600'
                                        : 'bg-emerald-500'
                                    : 'bg-slate-200'
                                )}
                              />
                            ))}
                          </div>
                          {passwordStrength.label && (
                            <p className="text-xs text-slate-500">
                              Strength: {passwordStrength.label}
                            </p>
                          )}
                          <ul className="space-y-1">
                            {requirements.map((r, i) => (
                              <li
                                key={i}
                                className={cn(
                                  'flex items-center gap-2 text-xs transition-colors duration-200',
                                  r.met ? 'text-emerald-600' : 'text-slate-500'
                                )}
                              >
                                <span
                                  className={cn(
                                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                                    r.met ? 'bg-emerald-100' : 'bg-slate-100'
                                  )}
                                >
                                  {r.met ? <Check className="h-2.5 w-2.5" /> : null}
                                </span>
                                {r.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="pt-1">
                      <Button
                        type="submit"
                        className={cn(
                          'w-full h-11 rounded-lg font-medium',
                          'bg-brand-900 hover:bg-brand-800 text-white',
                          'shadow-sm hover:shadow-md active:scale-[0.99]',
                          'transition-all duration-200'
                        )}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            Creating account...
                          </span>
                        ) : (
                          'Create account'
                        )}
                      </Button>
                    </div>
                  </form>

                  <p className="mt-6 text-center text-sm text-slate-600">
                    Already have an account?{' '}
                    <Link
                      to="/login"
                      className="font-medium text-brand-900 hover:text-brand-800 transition-colors underline-offset-2 hover:underline"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>

                {/* Mobile trust badges */}
                <div className="lg:hidden mt-6 flex flex-wrap justify-center gap-2">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    Free forever
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    No credit card
                  </span>
                </div>
              </div>
            </div>

            {/* Trust panel - desktop */}
            <div className="hidden lg:block order-1 lg:order-2">
              <div className="max-w-md lg:ml-auto space-y-6 animate-fade-in">
                <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-brand-50/30 border border-slate-200/80 p-8">
                  <h2 className="text-lg font-semibold text-slate-900">
                    Your financial data, secured
                  </h2>
                  <p className="mt-2 text-slate-600 text-sm">
                    Hoito keeps your assets and liabilities private with industry-standard security.
                  </p>
                  <div className="mt-6 space-y-4">
                    {trustItems.map((item) => (
                      <div
                        key={item.title}
                        className="flex gap-4 items-start p-3 rounded-xl bg-white/60 border border-slate-200/60 hover:bg-white hover:border-slate-300/80 transition-all duration-200"
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-brand-900 flex items-center justify-center">
                          <item.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-900 text-sm">{item.title}</h3>
                          <p className="mt-0.5 text-slate-600 text-xs">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    Free forever
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                    256-bit encryption
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </PublicLayout>
  );
}
